import { useTranslate } from "#src/hooks";
import { useOne } from "@refinedev/core";
import { Button } from "@libertify/ui";
import { Flex, Modal, Popconfirm } from "antd";
import {
  ContactResponse,
  MediaProjectResponse,
  PresenterProfileResponse,
} from "pages/media/types";
import { AvatarEditModal } from "pages/media/components/ContactWithPresenter/AvatarEditModal";
import SpeakerView from "pages/media/projects/components/SpeakerView";
import { useState } from "react";

export type ChapterAvatarModalProps = {
  open: boolean;
  projectId: string | undefined;
  currentContactId: string | undefined;
  onContactSelected: (contact: ContactResponse) => Promise<void>;
  onApplyToAllChapters: (contact?: ContactResponse) => Promise<void>;
  onContactEdited: (
    contact: ContactResponse,
    updatedPresenter: PresenterProfileResponse | undefined
  ) => Promise<void>;
  onClose: () => void;
};

export const ChapterAvatarModal = ({
  open,
  projectId,
  currentContactId,
  onContactSelected,
  onApplyToAllChapters,
  onContactEdited,
  onClose,
}: ChapterAvatarModalProps) => {
  const t = useTranslate();

  const [selectedContact, setSelectedContact] = useState<
    ContactResponse | undefined
  >();
  const [editingContact, setEditingContact] = useState<
    ContactResponse | undefined
  >();

  const { data: projectData } = useOne<MediaProjectResponse>({
    resource: "media/projects",
    id: projectId,
    queryOptions: { enabled: !!projectId && open },
  });

  const project = projectData?.data
    ? {
        ...projectData.data,
        settings: {
          ...projectData.data.settings,
          contact_id: currentContactId ?? projectData.data.settings.contact_id,
        },
      }
    : null;

  const handleClose = () => {
    setSelectedContact(undefined);
    onClose();
  };

  return (
    <Modal
      open={open}
      width="80vw"
      style={{ maxWidth: 1000 }}
      onCancel={handleClose}
      destroyOnHidden
      // react-moveable's control box (the selected item's resize handles on
      // the canvas behind this modal) has a hardcoded z-index: 3000, above
      // antd's default Modal z-index (~1000) — bump this modal above it.
      zIndex={3100}
      footer={
        <Flex justify="center" gap={12} wrap="wrap" style={{ width: "100%" }}>
          <Button
            variant="primary"
            disabled={!selectedContact}
            onClick={() => {
              if (!selectedContact) return;
              void onContactSelected(selectedContact);
              handleClose();
            }}
          >
            {t(
              "components.templates.settings.presenterTab.applyAvatarToCurrentChapter"
            )}
          </Button>
          <Popconfirm
            title={t(
              "components.templates.settings.presenterTab.changeAvatarForWholeVideoConfirm"
            )}
            onConfirm={() => {
              // No selection yet — apply the avatar already assigned to
              // this chapter to the whole video instead.
              void onApplyToAllChapters(selectedContact);
              handleClose();
            }}
          >
            <Button variant="outlined">
              {t(
                "components.templates.settings.presenterTab.applyAvatarToWholeVideo"
              )}
            </Button>
          </Popconfirm>
        </Flex>
      }
      title={t(
        "components.templates.settings.presenterTab.changeAvatarModalTitle"
      )}
    >
      <SpeakerView
        key={currentContactId}
        project={project}
        initialStep="SELECT_AVATAR"
        initialVoiceOnly={false}
        showNavigation={false}
        showHeader={false}
        itemsPerSlide={2}
        contentStyle={{ paddingBottom: 20 }}
        showMoreFields={false}
        onContactChange={(
          _contactId: string,
          _isVoiceOnly: boolean,
          contact?: ContactResponse
        ) => {
          if (!contact) return;
          setSelectedContact(contact);
        }}
        onEditContact={(contact) => setEditingContact(contact)}
      />
      <AvatarEditModal
        open={!!editingContact}
        contact={editingContact}
        onClose={() => setEditingContact(undefined)}
        onSaved={async (contact, updatedPresenter) => {
          await onContactEdited(contact, updatedPresenter);
          setEditingContact(undefined);
        }}
      />
    </Modal>
  );
};
